import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAppDispatch } from '../../../../app/hooks';
import { tryToAddAgent, tryToEditAgent } from '../../../../store/brokers/partner/agents/agentsSlice';
import SelectCheckbox from '../../../../shared/select-checkbox';
import Button from '../../../../shared/button';
import Input from '../../../../shared/input';
import Error from '../../../../shared/error';
import Modal from '../../../../shared/modal';

const accessTypes = [
    { label: 'Admin', value: 'partners' },
    { label: 'Non-Admin', value: 'agents' },
]

type FormValues = {
    firstName: string;
    lastName: string;
    email?: string;
    username?: string;
    password?: string;
};

interface IAgentManageModal {
    openModal: boolean;
    id?: string;
    data: any;
    handleCloseModal: () => void;
    onSuccessSaved: (type: string, message: string) => void;
}

const AgentManageModal = ({
    openModal,
    id,
    data,
    onSuccessSaved,
    handleCloseModal,
}: IAgentManageModal) => {
    const dispatch = useAppDispatch();
    const [createError, setCreateError] = useState<string | null>()
    const [type, setType] = useState<any>();

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
    } = useForm<FormValues>({});


    useEffect(() => {
        if (openModal && id && data) {
            setValue('email', data?.email)
            setValue('firstName', data?.firstName || data?.name)
            setValue('lastName', data?.lastName)
            setValue('username', data?.username)
            const findType = data?.cognitoGroups?.length > 1 ? accessTypes[0] : accessTypes[1]
            setType(findType)
        }
    }, [id, data, openModal])

    const onCloseModal = () => {
        reset();
        handleCloseModal();
    }

    const onSubmit = async (data: FormValues) => {
        if (!type) {
            setCreateError('Please select access type to continue!')
            return;
        }
        if (!id && !data?.username) {
            setCreateError('Username is required!')
            return;
        }
        if (!id && !data?.email) {
            setCreateError('Email is required!')
            return;
        }
        setCreateError(undefined)
        const payload: any = {
            ...data,
            enabled: false,
            username: (data?.username || '').toLowerCase(),
            accessType: type?.value
        };
        try {
            if (!id) {
                await dispatch(tryToAddAgent(payload)).unwrap();
            } else {
                await dispatch(tryToEditAgent({ id: id, data: payload })).unwrap();
            }
            reset();
            onSuccessSaved('success', 'Changes saved successfully')
            onCloseModal()
            setCreateError(null)
        } catch (error) {
            setCreateError(`${error}`)
        }
    };

    const onChangeSelected = (e: any, type?: string) => {
        setType(e)
    }

    return (
        <Modal
            open={openModal}
            showInRight
            contentContainerStyle='!min-w-[100vw] md:!min-w-[650px]'
            onClose={onCloseModal}>
            <div className={'p-2 min-w-[95vw] md:min-w-[600px]'}>
                <p className='mb-4 font-semibold text-sm'>Manage Member</p>
                {createError && <Error text={createError} />}
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className='bg-[#e4ede657] rounded-[6px] p-3 my-9'>
                        <div className='grid grid-cols-2 gap-4 content-center'>
                            <Input
                                label='First Name'
                                placeholder=' '
                                dataQa={'name'}
                                register={register('firstName', {
                                    required: {
                                        message: 'First Name is required',
                                        value: true,
                                    },
                                    validate: (value: string) => !!value.trim() || 'First Name is required'
                                })}
                                error={errors.firstName?.message}
                            />
                            <Input
                                label='Last Name'
                                placeholder=' '
                                dataQa={'name'}
                                register={register('lastName', {
                                    required: {
                                        message: 'Last Name is required',
                                        value: true,
                                    },
                                    validate: (value: string) => !!value.trim() || 'Last Name is required'
                                })}
                                error={errors.lastName?.message}
                            />
                            <Input
                                label='Username'
                                placeholder=' '
                                dataQa={'username'}
                                register={register('username', {
                                    required: {
                                        message: 'Username is required',
                                        value: true,
                                    },
                                    pattern: {
                                        /* eslint-disable-next-line */
                                        value: /^\S*$/,
                                        message: 'Username should not include spaces'
                                    },
                                })}
                                error={errors.username?.message}
                            />
                            <SelectCheckbox
                                name='Access Type'
                                placeholder=' '
                                selectedOption={type}
                                options={accessTypes}
                                onChangeSelectedOption={onChangeSelected}
                            />
                            <Input
                                label='Email'
                                placeholder=' '
                                dataQa={'email'}
                                disabled={!!id}
                                register={register('email', {
                                    required: {
                                        message: 'Email is required',
                                        value: true,
                                    },
                                    pattern: {
                                        /* eslint-disable-next-line */
                                        value: /^(([^<>()[\]\\.,;:\s@\']+(\.[^<>()[\]\\.,;:\s@\']+)*)|(\'.+\'))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                                        message: 'Please write a valid email'
                                    },
                                })}
                                error={errors.email?.message}
                            />
                            <Input
                                label='Password'
                                placeholder=' '
                                disabled={!!id}
                                dataQa={'password'}
                                type={'password'}
                                register={register('password', {
                                    minLength: {
                                        value: 8,
                                        message: 'Minimum number of characters is 8'
                                    },
                                    pattern: {
                                        value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                                        message: 'Password should be at least 8 characters long, and must contain at least 1 number, 1 lower case, 1 upper case and 1 special character(@$!%*?&)'
                                    },
                                },
                                )}
                                error={errors.password?.message}
                            />
                        </div>
                        <div className='flex flex-row justify-center py-2'>
                            <Button
                                label={id ? 'Save Changes' : 'Create Member'}
                                dataQa={'create-agent'}
                                type={'submit'}
                                className={'btn-primary-rounded-less'}
                            />
                        </div>
                    </div>
                </form>
            </div>
        </Modal>
    )
}
export default AgentManageModal;